import NavButton from '@atoms/NavButton/NavButton';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import { IFeaturedArticleCard } from '@models/IFeaturedArticleCard';
import { IMeta } from '@models/IMeta';
import { IProductCard } from '@models/IProductCard';
import { IPromoCard } from '@models/IPromoCard';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import ListingCardTypeEnum from '@models/enums/ListingCardTypeEnum';
import Carousel from '@molecules/Carousel/Carousel';
import FeaturedArticleCard from '@molecules/FeaturedArticleCard/FeaturedArticleCard';
import GridProductCard from '@molecules/GridProductCard/GridProductCard';
import PromoCard from '@molecules/PromoCard/PromoCard';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly title: string;
  readonly featureItem: IPromoCard | IProductCard | IFeaturedArticleCard | null;
  readonly otherItems: (IProductCard | null)[];
  readonly meta?: IMeta;
}

const FeaturedProductsGridTab = ({
  title,
  featureItem,
  otherItems,
  meta,
}: Props) => {
  const [isSwiperBeginning, setIsSwiperBeginning] = useState(true);
  const [isSwiperEnd, setIsSwiperEnd] = useState(false);
  const isMobileViewport = useMediaQuery(1440);
  const isMobileScreen = useMediaQuery(1440, BreakpointTypeEnum.Screen);
  const isZoomedDesktop = !isMobileScreen && isMobileViewport;
  const screenBreakpoint = useBreakpoint(BreakpointTypeEnum.Screen);
  const isTouchDevice = useIsTouchDevice();
  const navButtonId = `featured-products-grid`;
  const isPreview = usePreviewMode();

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key={title}>
      {isPreview && (
        <div
          className="body-one mb-6 text-center text-white"
          data-epi-property-name={meta?.editHints?.title}
        >
          {title}
        </div>
      )}
      {isMobileViewport ? (
        <div className="flex flex-col gap-7.5">
          {featureItem && featureItem.type === ListingCardTypeEnum.Promo && (
            <PromoCard
              {...(featureItem as IPromoCard)}
              isLargeCard
              dataTestId="CT-164-Featured-Product-Grid-Main-Promo-Card"
              epiEdit={meta?.editHints?.featureItem as string}
            />
          )}

          {featureItem && featureItem.type === ListingCardTypeEnum.Product && (
            <GridProductCard
              {...(featureItem as IProductCard)}
              isLargeCard
              dataTestId="CT-164-Featured-Product-Grid-Main-Product-Card"
              epiEdit={meta?.editHints?.featureItem as string}
            />
          )}
          {featureItem && featureItem.type === ListingCardTypeEnum.Article && (
            <FeaturedArticleCard
              {...(featureItem as IFeaturedArticleCard)}
              dataTestId="CT-164-Featured-Product-Grid-Main-Article-Card"
              epiEdit={meta?.editHints?.featureItem as string}
            />
          )}

          <div
            className="relative w-full"
            data-epi-property-name={meta?.editHints?.otherItems}
          >
            <ShadowContainerHorizontal
              showStart={!isSwiperBeginning}
              showEnd={!isSwiperEnd}
              disableScroll
            >
              <Carousel
                id="Featured-Products-Grid"
                analyticsEnabled
                spaceBetween={20}
                slidesPerView={1.3}
                navId={navButtonId}
                className="whitespace-normal"
                breakpoints={{
                  768: {
                    slidesPerView: 1.6,
                  },
                  1024: {
                    slidesPerView: 2.3,
                  },
                }}
                onSetIsSwiperBeginning={setIsSwiperBeginning}
                onSetIsSwiperEnd={setIsSwiperEnd}
              >
                {otherItems
                  .filter((tiledCard) => tiledCard !== null)
                  .map((item, index) => (
                    <SwiperSlide className="stretch-card" key={item.name}>
                      <GridProductCard
                        {...item}
                        dataTestId={`CT-164-Featured-Product-Grid-Product-Card-${index}`}
                      />
                    </SwiperSlide>
                  ))}
              </Carousel>
            </ShadowContainerHorizontal>

            <div
              className={classNames(
                'mt-6 items-center justify-end',
                isZoomedDesktop ||
                  (screenBreakpoint === BreakpointEnum.Large && !isTouchDevice)
                  ? 'flex'
                  : 'hidden'
              )}
            >
              <div className="flex gap-x-[25px]">
                <NavButton
                  direction={HorizontalDirectionEnum.Left}
                  id={navButtonId}
                  disabled={isSwiperBeginning}
                />

                <NavButton
                  direction={HorizontalDirectionEnum.Right}
                  id={navButtonId}
                  disabled={isSwiperEnd}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-8 grid-rows-2 gap-4">
          {featureItem && (
            <div
              className="col-span-4 row-span-2"
              data-epi-property-name={meta?.editHints?.featureItem}
            >
              {featureItem.type === ListingCardTypeEnum.Promo && (
                <PromoCard
                  {...(featureItem as IPromoCard)}
                  isLargeCard
                  dataTestId="CT-164-Featured-Product-Grid-Main-Promo-Card"
                  epiEdit={meta?.editHints?.featureItem as string}
                />
              )}

              {featureItem.type === ListingCardTypeEnum.Product && (
                <GridProductCard
                  {...(featureItem as IProductCard)}
                  isLargeCard
                  dataTestId="CT-164-Featured-Product-Grid-Main-Product-Card"
                  epiEdit={meta?.editHints?.featureItem as string}
                />
              )}

              {featureItem.type === ListingCardTypeEnum.Article && (
                <FeaturedArticleCard
                  {...(featureItem as IFeaturedArticleCard)}
                  dataTestId="CT-164-Featured-Product-Grid-Main-Article-Card"
                  epiEdit={meta?.editHints?.featureItem as string}
                />
              )}
            </div>
          )}

          {otherItems
            .filter((tiledCard) => tiledCard !== null)
            .map((tiledCard, index) => (
              <div
                className="col-span-2"
                data-epi-property-name={meta?.editHints?.otherItems}
                key={tiledCard.name}
              >
                <GridProductCard
                  {...tiledCard}
                  dataTestId={`CT-164-Featured-Product-Grid-Product-Card-${index}`}
                />
              </div>
            ))}
        </div>
      )}
    </motion.div>
  );
};

export default FeaturedProductsGridTab;
